// Keycloak token plumbing, and the one call that is not a chat call.
//
// What used to be here — send, stream, resume, conversations, assets, mocks —
// belonged to the chat stack that the Responses API replaced. Conversations
// live in conversations.js and runs in responses.js; the asset calls pointed at
// routes that no longer exist.

const API_BASE = '/api/v1';

// Store keycloak instance reference. A promise-based ready signal lets the
// auth provider expose the instance asynchronously: callers can await it
// instead of racing past an unset singleton — which is what was producing
// "JWT is missing" 400s on every reload in ui-admin and could in ui-chatbot.
let keycloakInstance = null;
let resolveReady = null;
const keycloakReady = new Promise((resolve) => { resolveReady = resolve; });

/** Extract error message from API error response (nested error.Message / error.message / message). */
function getApiErrorMessage(errorData) {
  if (typeof errorData?.error === 'string') return errorData.error;
  return errorData?.error?.Message || errorData?.error?.message || errorData?.message;
}

// Set keycloak instance (called from KeycloakProvider)
export function setKeycloakInstance(keycloak) {
  keycloakInstance = keycloak;
}

// Signal that the Keycloak instance is mounted and usable. Resolves the
// promise that getAuthToken awaits on its first call.
export function resolveKeycloakReady(keycloak) {
  keycloakInstance = keycloak;
  if (resolveReady) {
    resolveReady(keycloak);
    resolveReady = null;
  }
}

// Function to get the Keycloak access token (with automatic refresh if needed).
// The backend is an OAuth resource server, so it's authorized with the access token
// (aud: oplus via an audience mapper; groups + realm_access.roles via protocol mappers).
// Waits for KeycloakProvider to expose the singleton — without this, the very
// first request after a page reload can fire before keycloak.init resolves
// (the StrictMode race) and end up sending no Authorization header at all.
export async function getAuthToken() {
  if (!keycloakInstance) {
    await keycloakReady;
  }
  if (!keycloakInstance) return null;

  try {
    await keycloakInstance.updateToken(30);
    return keycloakInstance.token;
  } catch (error) {
    // No refresh token (PKCE without offline_access) is common; if the token
    // hasn't actually expired yet we can still use it.
    const expiresIn = keycloakInstance.tokenParsed
      ? keycloakInstance.tokenParsed.exp - Math.floor(Date.now() / 1000)
      : 0;
    if (expiresIn > 0) {
      return keycloakInstance.token;
    }
    // Both tokens dead — silent re-auth via login() (NOT logout()). KC's SSO
    // cookie usually still bounces the user back without a credential prompt.
    if (keycloakInstance.authenticated) {
      keycloakInstance.login({ redirectUri: window.location.href });
    }
    return null;
  }
}

// Get projects with their bots (NEW API - replaces getBotsAPI)
// Returns: { projects: [{ name, title, bots: [{ name, title }] }] }
export const getProjectsAndBotsAPI = async () => {
  const token = await getAuthToken();
  
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE}/chat/bots`, {
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = getApiErrorMessage(errorData);
    if (errorMessage) throw new Error(errorMessage);
    throw new Error(`API Error: ${response.status}`);
  }

  return await response.json();
};
